import { CircleAlertIcon } from "lucide-react";
import {
  ComboBox,
  FieldError,
  Input,
  Label,
  Button as RACButton,
  TooltipTrigger,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";

import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import {
  ComboBoxListBox,
  ComboBoxListItem,
  ComboBoxPopover,
} from "@/components/combobox";
import { ChevronDownIcon } from "@/components/icons";
import { Tooltip } from "@/components/tooltip";
import {
  getExtensionFromFilename,
  parseFileName,
} from "@/utils/getExtensionFromMime.ts";

import { useConfidenceCheckerMyFiles } from "../hooks/useConfidenceCheckerMyFiles";

interface SessionFileNameComboboxProps {
  displayBaseName: string;
  isDisabled?: boolean;
  onChange: (
    value: {
      displayBaseName: string;
      previousVersionId: null | number;
    },
    options?: { immediate?: boolean },
  ) => void;
  originalFileName: string;
  previousVersionId: null | number;
  requestId: number;
}

export function SessionFileNameCombobox({
  displayBaseName,
  isDisabled = false,
  onChange,
  originalFileName,
  previousVersionId,
  requestId,
}: SessionFileNameComboboxProps) {
  const { data: myFiles = [] } = useConfidenceCheckerMyFiles(requestId);

  const isInvalid = displayBaseName.trim() === "";

  const currentFileExtension = getExtensionFromFilename(originalFileName);

  const filteredFiles = myFiles.filter(
    (file) => getExtensionFromFilename(file.name) === currentFileExtension,
  );

  const handleInputChange = (value: string) => {
    const matchedFile = filteredFiles.find((file) => {
      try {
        const { baseName } = parseFileName(file.name);

        return baseName === value;
      } catch {
        return false;
      }
    });

    onChange({
      displayBaseName: value,
      previousVersionId: matchedFile?.id ?? null,
    });
  };

  const handleSelectionChange = (selectedFileId: null | number | string) => {
    if (selectedFileId === null) return;

    const fileId = Number(selectedFileId);
    const selectedFile = filteredFiles.find((file) => file.id === fileId);

    if (!selectedFile) return;

    try {
      const { baseName } = parseFileName(selectedFile.name);

      onChange(
        {
          displayBaseName: baseName,
          previousVersionId: fileId,
        },
        { immediate: true },
      );
    } catch {
      onChange(
        {
          displayBaseName: selectedFile.name,
          previousVersionId: fileId,
        },
        { immediate: true },
      );
    }
  };

  return (
    <ComboBox
      allowsCustomValue
      className="flex min-w-0 flex-1 flex-col gap-[4px]"
      defaultItems={filteredFiles}
      inputValue={displayBaseName}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      onInputChange={handleInputChange}
      onSelectionChange={handleSelectionChange}
      selectedKey={previousVersionId ?? undefined}
    >
      <div className="flex items-center justify-between gap-2">
        <Label className="truncate text-xs font-regular text-text-tertiary">
          {originalFileName}
        </Label>
        {previousVersionId !== null && (
          <TooltipTrigger delay={0}>
            <RACButton>
              <CircleAlertIcon className="text-fg-brand-primary" size={12} />
            </RACButton>
            <Tooltip>You&apos;re updating the previous version file</Tooltip>
          </TooltipTrigger>
        )}
      </div>
      <div className="relative min-w-0">
        <Input
          className={({ isFocused }) =>
            twMerge(
              "h-4xl w-full rounded-md border border-border-primary bg-bg-primary py-sm pl-lg pr-[30px] text-sm font-regular text-text-primary outline-none",
              "placeholder:text-text-placeholder",
              isFocused && "border-border-brand ring-4 ring-border-brand/25",
              isInvalid && "border-border-error ring-4 ring-border-error/25",
            )
          }
          placeholder="Set file name or choose from list"
        />
        <Button
          className="absolute right-0 top-1/2 -translate-y-1/2"
          data-ui="trigger"
          isDisabled={isDisabled}
          isIconOnly
          size="sm"
          variant="plain"
        >
          <ChevronDownIcon className="text-text-quaternary" strokeWidth={2} />
        </Button>
      </div>
      <FieldError className="text-xs text-text-error-primary">
        File name cannot be blank. Please input a name.
      </FieldError>
      {filteredFiles.length > 0 && (
        <ComboBoxPopover>
          <ComboBoxListBox items={filteredFiles}>
            {(item) => {
              let label = item.name;

              try {
                const { fullNormalizedName } = parseFileName(item.name);

                label = fullNormalizedName;
              } catch {
                label = item.name;
              }

              return (
                <ComboBoxListItem id={item.id}>
                  <div className="flex w-[310px] items-center justify-between gap-2">
                    <div className="flex min-w-0 flex-1 gap-1">
                      <span className="truncate">{label}</span>
                      <span className="whitespace-nowrap">
                        (ver: {item.version ?? 1})
                      </span>
                    </div>
                    {item.isSourceFile && (
                      <Badge isFilled isRounded size="small" variant="brand">
                        Source file
                      </Badge>
                    )}
                  </div>
                </ComboBoxListItem>
              );
            }}
          </ComboBoxListBox>
        </ComboBoxPopover>
      )}
    </ComboBox>
  );
}
